import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { Field, Input, Label } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import {
  useCreateMailboxRouteMutation,
  useDeactivateMailboxRouteMutation,
  useListMailboxRoutesQuery,
  useUpdateMailboxRouteMutation,
  type MailboxRoute,
  type MailboxRouteInput,
} from '@shared/api';

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow-x: auto;
  table {
    min-width: 900px;
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DescCell = styled.div`
  max-width: 480px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: flex-end;
  flex-wrap: nowrap;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const DescriptionInput = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font: inherit;
  font-size: 14px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -1px;
  }
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const emptyInput = (): MailboxRouteInput => ({
  email: '',
  display_name: '',
  description: '',
  department: '',
  priority: 0,
  is_active: true,
});

export function MailboxRoutesTab() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, error } = useListMailboxRoutesQuery({
    include_inactive: includeInactive,
  });
  const [createMutation, createState] = useCreateMailboxRouteMutation();
  const [updateMutation, updateState] = useUpdateMailboxRouteMutation();
  const [deactivateMutation] = useDeactivateMailboxRouteMutation();

  const [editing, setEditing] = useState<MailboxRoute | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<MailboxRouteInput>(emptyInput());
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const openCreate = () => {
    setForm(emptyInput());
    setFormError(null);
    setCreating(true);
  };
  const openEdit = (r: MailboxRoute) => {
    setEditing(r);
    setForm({
      email: r.email,
      display_name: r.display_name,
      description: r.description,
      department: r.department ?? '',
      priority: r.priority,
      is_active: r.is_active,
    });
    setFormError(null);
  };
  const closeModal = () => {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const payload: MailboxRouteInput = {
      ...form,
      department: form.department?.trim() || null,
    };
    try {
      if (editing) {
        await updateMutation({ id: editing.id, input: payload }).unwrap();
      } else {
        await createMutation(payload).unwrap();
      }
      closeModal();
    } catch (err: unknown) {
      const message =
        (err as { data?: { detail?: string }; message?: string })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Не удалось сохранить ящик. Проверьте поля.';
      setFormError(message);
    }
  };

  const items = data?.items ?? [];

  return (
    <div>
      <Toolbar>
        <div>
          <Label as="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Показывать отключённые
          </Label>
        </div>
        <Button $variant="primary" onClick={openCreate}>
          + Новый ящик отдела
        </Button>
      </Toolbar>

      {error && <ErrorBox>Не удалось загрузить список ящиков.</ErrorBox>}

      <TableCard>
        {isLoading ? (
          <EmptyState>Загрузка…</EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>
            Ящики отделов пока не настроены. Создайте первый — на основе его
            описания агент-диспетчер начнёт маршрутизировать письма.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Отдел</th>
                <th>Email</th>
                <th>Описание (учится агент)</th>
                <th>Приоритет</th>
                <th>Статус</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.display_name}</td>
                  <td>
                    <Mono>{r.email}</Mono>
                  </td>
                  <td>
                    <DescCell title={r.description}>{r.description}</DescCell>
                  </td>
                  <td>{r.priority}</td>
                  <td>
                    {r.is_active ? (
                      <Badge $tone="success">активен</Badge>
                    ) : (
                      <Badge $tone="neutral">отключён</Badge>
                    )}
                  </td>
                  <td>
                    <ActionsCell>
                      <Button $size="sm" $variant="ghost" onClick={() => openEdit(r)}>
                        Редактировать
                      </Button>
                      {r.is_active && (
                        <Button
                          $size="sm"
                          $variant="danger"
                          onClick={() => setConfirmDeleteId(r.id)}
                        >
                          Отключить
                        </Button>
                      )}
                    </ActionsCell>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      <Modal
        open={creating || editing !== null}
        onClose={closeModal}
        title={editing ? `Ящик ${editing.display_name}` : 'Новый ящик отдела'}
      >
        <form onSubmit={submit}>
          {formError && <ErrorBox>{formError}</ErrorBox>}
          <FormGrid>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="legal@company.ru"
                required
              />
            </Field>
            <Field>
              <Label>Название отдела</Label>
              <Input
                value={form.display_name}
                onChange={(e) =>
                  setForm({ ...form, display_name: e.target.value })
                }
                placeholder="Юридический отдел"
                required
              />
            </Field>
            <Field>
              <Label>Подразделение (опционально)</Label>
              <Input
                value={form.department ?? ''}
                onChange={(e) =>
                  setForm({ ...form, department: e.target.value })
                }
                placeholder="Дирекция по правовым вопросам"
              />
            </Field>
            <Field>
              <Label>Приоритет</Label>
              <Input
                type="number"
                value={form.priority ?? 0}
                onChange={(e) =>
                  setForm({ ...form, priority: Number(e.target.value) || 0 })
                }
                min={0}
                max={100}
              />
            </Field>
            <Field>
              <Label as="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
                Активен
              </Label>
            </Field>
            <FullRow>
              <Field>
                <Label>Описание</Label>
                <DescriptionInput
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Опишите тематику ящика: какие письма должны попадать сюда, типы вложений, типичных отправителей. Пример: «Все письма о тендерах, конкурсах, закупках по 44-ФЗ/223-ФЗ. Вложения — техническая документация (РД, ТЗ, БК). Типичные отправители: zakupki.gov.ru, etp-ets.ru, *@tender.»"
                  required
                  minLength={30}
                />
                <Hint>
                  На основе этого описания агент-диспетчер решает, какие письма
                  направлять в этот ящик. Чем подробнее описание — тем точнее
                  маршрутизация. Минимум 30 символов.
                </Hint>
              </Field>
            </FullRow>
          </FormGrid>
          <ModalActions>
            <Button type="button" $variant="ghost" onClick={closeModal}>
              Отмена
            </Button>
            <Button
              type="submit"
              $variant="primary"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {editing ? 'Сохранить изменения' : 'Создать ящик'}
            </Button>
          </ModalActions>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Отключить ящик?"
        warning="Письма перестанут автоматически уходить на этот ящик. История пересылок сохранится, ящик можно вернуть в работу включением 'Активен'."
        confirmText={
          items.find((r) => r.id === confirmDeleteId)?.email ?? ''
        }
        confirmLabel="Введите email ящика для подтверждения"
        confirmButtonLabel="Отключить"
        busyLabel="Отключение…"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (confirmDeleteId) {
            await deactivateMutation(confirmDeleteId).unwrap().catch(() => {});
          }
          setConfirmDeleteId(null);
        }}
      />
    </div>
  );
}
